import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import posthog from 'posthog-js';
import {
  CalendarDays,
  Clock3,
  LogOut,
  FolderKanban,
  Maximize2,
  Minimize2,
  Monitor,
  Moon,
  Search,
  Square,
  Sun,
  Trophy,
  UserCircle2,
  WalletCards,
  X,
  BookOpenText,
  Image,
  Palette,
  Settings2,
  Upload,
} from 'lucide-react';
import { auth, onAuthStateChanged, signOut } from './firebase';
import { getWorks, getWorkspaces } from './storage';
import { applyTheme, themes } from './themes';
import { ensureLeaderboardEntry, getMyPoints } from './points';
import { getLocalPreferences, getUserPreferences } from './preferences';
import './App.css';

import Topbar from './components/Topbar';
import Sidebar from './components/Sidebar';
import AuthScreen from './components/AuthScreen';
import Dashboard from './components/Dashboard';
import WorkPage from './components/WorkPage';
import ProfilePage from './components/ProfilePage';
import PublicProfilePage from './components/PublicProfilePage';
import Leaderboard from './components/Leaderboard';
import FriendsPanel from './components/FriendsPanel';
import SettingsPage from './components/SettingsPage';
import PomodoroTimer from './components/PomodoroTimer';
import UpcomingPage from './components/UpcomingPage';
import ExpenseTracker from './components/ExpenseTracker';
import BottomNav from './components/BottomNav';
import NotebooksPage from './components/NotebooksPage';

const THEME_KEY = 'gwd_theme';
const GUEST_KEY = 'gwd_guest';
const NAV_KEY = 'gwd_nav';
const SIDEBAR_KEY = 'gwd_sidebar';
const DESKTOP_ICON_KEY = 'gwd_desktop_icons';
const DESKTOP_WALLPAPER_KEY = 'gwd_desktop_wallpaper';
const DESKTOP_THEME_OPTIONS = ['desktop-mode', 'desktop-dark', 'desktop-light'];

const WALLPAPER_PRESETS = [
  { id: 'default', name: 'Default', value: null },
  { id: 'aurora', name: 'Aurora', value: 'linear-gradient(135deg, #0f0c29 0%, #302b63 50%, #24243e 100%)' },
  { id: 'sunset', name: 'Sunset', value: 'linear-gradient(135deg, #f5af19 0%, #f12711 50%, #c31432 100%)' },
  { id: 'ocean', name: 'Ocean', value: 'linear-gradient(135deg, #2193b0 0%, #6dd5ed 50%, #1a2980 100%)' },
  { id: 'forest', name: 'Forest', value: 'linear-gradient(135deg, #134e5e 0%, #71b280 50%, #0f3443 100%)' },
  { id: 'lavender', name: 'Lavender', value: 'linear-gradient(135deg, #667eea 0%, #764ba2 50%, #f093fb 100%)' },
  { id: 'midnight', name: 'Midnight', value: 'linear-gradient(135deg, #0f2027 0%, #203a43 50%, #2c5364 100%)' },
];

function getInitials(user) {
  if (!user) return 'G';
  if (user.displayName) return user.displayName.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase();
  return user.email?.[0]?.toUpperCase() || 'U';
}

function readNav() {
  try { return JSON.parse(sessionStorage.getItem(NAV_KEY)) || {}; }
  catch { return {}; }
}

function saveNav(state) {
  sessionStorage.setItem(NAV_KEY, JSON.stringify(state));
}

const DESKTOP_WINDOW_DEFAULTS = {
  dashboard: { title: 'Get Work Done', x: 84, y: 72, width: 920, height: 640 },
  upcoming: { title: 'Upcoming', x: 180, y: 108, width: 760, height: 540 },
  pomodoro: { title: 'Pomodoro Timer', x: 260, y: 92, width: 580, height: 620 },
  'expense-tracker': { title: 'Finance Tracker', x: 318, y: 116, width: 860, height: 600 },
  profile: { title: 'Profile', x: 208, y: 88, width: 760, height: 600 },
  leaderboard: { title: 'Leaderboard', x: 236, y: 76, width: 720, height: 600 },
  settings: { title: 'Settings', x: 276, y: 90, width: 720, height: 620 },
  notebooks: { title: 'Notebooks', x: 132, y: 64, width: 1080, height: 680 },
  work: { title: 'Work', x: 148, y: 58, width: 1120, height: 700 },
  publicProfile: { title: 'Public Profile', x: 242, y: 84, width: 760, height: 600 },
};

const DESKTOP_ICON_DEFAULTS = {
  dashboard: { x: 24, y: 28 },
  upcoming: { x: 24, y: 140 },
  pomodoro: { x: 24, y: 252 },
  'expense-tracker': { x: 24, y: 364 },
  notebooks: { x: 24, y: 476 },
  leaderboard: { x: 132, y: 28 },
  profile: { x: 132, y: 140 },
  settings: { x: 132, y: 252 },
};

function readDesktopIcons() {
  try {
    return JSON.parse(localStorage.getItem(DESKTOP_ICON_KEY)) || {};
  } catch {
    return {};
  }
}

const DESKTOP_APP_ICONS = {
  dashboard: FolderKanban,
  upcoming: CalendarDays,
  pomodoro: Clock3,
  'expense-tracker': WalletCards,
  notebooks: BookOpenText,
  leaderboard: Trophy,
  profile: UserCircle2,
  settings: Settings2,
  work: FolderKanban,
  publicProfile: UserCircle2,
};

function DesktopWindowFrame({
  windowState,
  isFocused,
  onFocus,
  onMinimize,
  onToggleMaximize,
  onClose,
  onDragStart,
  children,
}) {
  const frameStyle = windowState.maximized ? {
    inset: '0 0 48px 0',
    width: 'auto',
    height: 'auto',
    transform: 'none',
  } : {
    width: windowState.width,
    height: windowState.height,
    transform: `translate3d(${windowState.x}px, ${windowState.y}px, 0)`,
  };

  const AppIcon = DESKTOP_APP_ICONS[windowState.id] || FolderKanban;

  return (
    <section
      className={`desktop-window ${isFocused ? 'focused' : ''} ${windowState.maximized ? 'maximized' : ''}`}
      style={{ ...frameStyle, zIndex: windowState.z }}
      onPointerDown={() => onFocus(windowState.id)}
    >
      <header
        className="desktop-window-titlebar"
        onPointerDown={event => {
          if (event.target.closest('button')) return;
          onDragStart(windowState.id, event);
        }}
        onDoubleClick={() => onToggleMaximize(windowState.id)}
      >
        <div className="desktop-window-title">
          <AppIcon size={14} />
          <span>{windowState.title}</span>
        </div>
        <div className="desktop-window-actions">
          <button type="button" className="desktop-window-btn win-min" onClick={() => onMinimize(windowState.id)} aria-label={`Minimize ${windowState.title}`}>
            <Minimize2 size={14} />
          </button>
          <button type="button" className="desktop-window-btn win-max" onClick={() => onToggleMaximize(windowState.id)} aria-label={`${windowState.maximized ? 'Restore' : 'Maximize'} ${windowState.title}`}>
            {windowState.maximized ? <Square size={12} /> : <Maximize2 size={14} />}
          </button>
          <button type="button" className="desktop-window-btn win-close" onClick={() => onClose(windowState.id)} aria-label={`Close ${windowState.title}`}>
            <X size={14} />
          </button>
        </div>
      </header>
      <div className="desktop-window-content">
        {children}
      </div>
    </section>
  );
}

export default function App() {
  const [authState, setAuthState] = useState('loading');
  const [user, setUser] = useState(null);
  const [works, setWorks] = useState([]);
  const [workspaces, setWorkspaces] = useState([]);
  const [myPoints, setMyPoints] = useState(null);
  const [page, setPageState] = useState(() => readNav().page || 'dashboard');
  const [openWork, setOpenWork] = useState(null);
  const [publicUid, setPublicUid] = useState(() => readNav().publicUid || null);
  const [activeWsId, setActiveWsId] = useState('general');
  const [showAddWork, setShowAddWork] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(
    () => localStorage.getItem(SIDEBAR_KEY) === 'true'
  );
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [theme, setThemeState] = useState(() => localStorage.getItem(THEME_KEY) || 'github-dark');
  const [guestPreferences, setGuestPreferences] = useState(() => getLocalPreferences());
  const [desktopNow, setDesktopNow] = useState(() => new Date());
  const [startMenuOpen, setStartMenuOpen] = useState(false);
  const [desktopIconPositions, setDesktopIconPositions] = useState(() => readDesktopIcons());
  const [selectedDesktopIcon, setSelectedDesktopIcon] = useState(null);
  const [desktopWallpaper, setDesktopWallpaper] = useState(() => localStorage.getItem(DESKTOP_WALLPAPER_KEY) || null);
  const [showPersonalize, setShowPersonalize] = useState(false);
  const [startMenuSearch, setStartMenuSearch] = useState('');
  const loggedIn = authState === 'authed' || authState === 'guest';
  const preferences = authState === 'guest' ? guestPreferences : getUserPreferences(myPoints);
  const notebooksEnabled = preferences.notebooksEnabled;
  const notebookSocialsEnabled = preferences.notebookSocialsEnabled;
  const inNotebooksApp = page === 'notebooks';
  const inProductApp = inNotebooksApp;
  const desktopMode = loggedIn && DESKTOP_THEME_OPTIONS.includes(theme);
  const win10Mode = theme === 'desktop-dark' || theme === 'desktop-light';
  const classicDesktopMode = theme === 'desktop-mode';
  const showSidebar = loggedIn && page !== 'work' && !inProductApp;
  const dragRef = useRef(null);
  const iconDragRef = useRef(null);
  const iconDragMovedRef = useRef(false);
  const startMenuRef = useRef(null);
  const desktopZRef = useRef(20);
  const wallpaperFileRef = useRef(null);

  const [desktopWindows, setDesktopWindows] = useState(() => {
    const base = DESKTOP_WINDOW_DEFAULTS.dashboard;
    return [{
      id: 'dashboard',
      title: base.title,
      x: base.x,
      y: base.y,
      width: base.width,
      height: base.height,
      minimized: false,
      maximized: false,
      z: 10,
    }];
  });

  function setPage(nextPage, extra = {}) {
    setPageState(nextPage);
    saveNav({ page: nextPage, ...extra });
    setMobileMenuOpen(false);
  }

  const setTheme = useCallback((nextTheme) => {
    setThemeState(nextTheme);
    setStartMenuOpen(false);
  }, []);

  const createWindowState = useCallback((windowId, titleOverride) => {
    const fallback = DESKTOP_WINDOW_DEFAULTS.dashboard;
    const base = DESKTOP_WINDOW_DEFAULTS[windowId] || fallback;
    return {
      id: windowId,
      title: titleOverride || base.title,
      x: base.x,
      y: base.y,
      width: base.width,
      height: base.height,
      minimized: false,
      maximized: false,
      z: desktopZRef.current,
    };
  }, []);

  const focusDesktopWindow = useCallback((windowId) => {
    setDesktopWindows(prev => {
      const target = prev.find(item => item.id === windowId);
      if (!target) return prev;
      const nextZ = desktopZRef.current + 1;
      desktopZRef.current = nextZ;
      return prev.map(item => item.id === windowId ? { ...item, z: nextZ, minimized: false } : item);
    });
  }, []);

  const openDesktopWindow = useCallback((windowId, titleOverride) => {
    setDesktopWindows(prev => {
      const existing = prev.find(item => item.id === windowId);
      const nextZ = desktopZRef.current + 1;
      desktopZRef.current = nextZ;
      if (existing) {
        return prev.map(item => item.id === windowId ? {
          ...item,
          title: titleOverride || item.title,
          minimized: false,
          z: nextZ,
        } : item);
      }
      return [...prev, { ...createWindowState(windowId, titleOverride), z: nextZ }];
    });
  }, [createWindowState]);

  const closeDesktopWindow = useCallback((windowId) => {
    setDesktopWindows(prev => prev.filter(item => item.id !== windowId));
  }, []);

  const minimizeDesktopWindow = useCallback((windowId) => {
    setDesktopWindows(prev => prev.map(item => item.id === windowId ? { ...item, minimized: true } : item));
  }, []);

  const toggleDesktopWindowMaximize = useCallback((windowId) => {
    setDesktopWindows(prev => prev.map(item => item.id === windowId ? {
      ...item,
      minimized: false,
      maximized: !item.maximized,
    } : item));
  }, []);

  const updateDesktopWindowTitle = useCallback((windowId, title) => {
    setDesktopWindows(prev => prev.map(item => item.id === windowId ? { ...item, title } : item));
  }, []);

  const startDesktopDrag = useCallback((windowId, event) => {
    const activeWindow = desktopWindows.find(item => item.id === windowId);
    if (!activeWindow || activeWindow.maximized) return;
    const boundsWidth = window.innerWidth;
    const boundsHeight = window.innerHeight - 74;
    dragRef.current = {
      id: windowId,
      pointerId: event.pointerId,
      offsetX: event.clientX - activeWindow.x,
      offsetY: event.clientY - activeWindow.y,
      boundsWidth,
      boundsHeight,
    };
    event.currentTarget.setPointerCapture?.(event.pointerId);
    focusDesktopWindow(windowId);
  }, [desktopWindows, focusDesktopWindow]);

  const navigateToPage = useCallback((nextPage, extra = {}) => {
    setPage(nextPage, extra);
    if (!desktopMode) return;
    if (nextPage === 'work') {
      openDesktopWindow('work', extra.title || openWork?.title || DESKTOP_WINDOW_DEFAULTS.work.title);
      return;
    }
    if (nextPage === 'publicProfile') {
      openDesktopWindow('publicProfile', extra.title || DESKTOP_WINDOW_DEFAULTS.publicProfile.title);
      return;
    }
    openDesktopWindow(nextPage, extra.title);
  }, [desktopMode, openDesktopWindow, openWork]);

  const desktopIcons = useMemo(() => {
    const items = [
      { id: 'dashboard', label: 'Projects', icon: FolderKanban },
      { id: 'upcoming', label: 'Calendar', icon: CalendarDays },
      { id: 'pomodoro', label: 'Timer', icon: Clock3 },
      { id: 'expense-tracker', label: 'Finance', icon: WalletCards },
      { id: 'leaderboard', label: 'Leaderboard', icon: Trophy },
      { id: 'profile', label: 'Profile', icon: UserCircle2 },
      { id: 'settings', label: 'Settings', icon: Settings2 },
    ];
    if (notebooksEnabled) {
      items.splice(4, 0, { id: 'notebooks', label: 'Notebooks', icon: BookOpenText });
    }
    return items;
  }, [notebooksEnabled]);

  const visibleDesktopWindows = desktopWindows.filter(item => !item.minimized);
  const focusedDesktopWindowId = visibleDesktopWindows.reduce((topId, item) => {
    if (!topId) return item.id;
    const top = visibleDesktopWindows.find(entry => entry.id === topId);
    return top && top.z > item.z ? topId : item.id;
  }, null);

  useEffect(() => {
    applyTheme(theme);
    localStorage.setItem(THEME_KEY, theme);
  }, [theme]);

  useEffect(() => {
    const current = readDesktopIcons();
    let changed = false;
    const next = { ...current };
    Object.entries(DESKTOP_ICON_DEFAULTS).forEach(([iconId, position]) => {
      if (!next[iconId]) {
        next[iconId] = position;
        changed = true;
      }
    });
    if (changed) {
      localStorage.setItem(DESKTOP_ICON_KEY, JSON.stringify(next));
      setDesktopIconPositions(next);
    }
  }, []);

  useEffect(() => {
    if (!desktopMode) return;
    openDesktopWindow('dashboard');
    if (page === 'work' && openWork) {
      openDesktopWindow('work', openWork.title);
    } else if (page === 'publicProfile' && publicUid) {
      openDesktopWindow('publicProfile');
    } else if (page !== 'dashboard') {
      openDesktopWindow(page);
    }
  }, [desktopMode, page, openWork, publicUid, openDesktopWindow]);

  useEffect(() => {
    if (!desktopMode || !openWork) return;
    updateDesktopWindowTitle('work', openWork.title);
  }, [desktopMode, openWork, updateDesktopWindowTitle]);

  useEffect(() => {
    if (!desktopMode) return undefined;
    const tick = window.setInterval(() => setDesktopNow(new Date()), 30000);
    function handlePointerMove(event) {
      const activeDrag = dragRef.current;
      if (!activeDrag) return;
      const nextX = Math.max(12, Math.min(event.clientX - activeDrag.offsetX, activeDrag.boundsWidth - 320));
      const nextY = Math.max(12, Math.min(event.clientY - activeDrag.offsetY, activeDrag.boundsHeight - 160));
      setDesktopWindows(prev => prev.map(item => item.id === activeDrag.id ? {
        ...item,
        x: nextX,
        y: nextY,
      } : item));
    }
    function handlePointerUp() {
      dragRef.current = null;
    }
    window.addEventListener('pointermove', handlePointerMove);
    window.addEventListener('pointerup', handlePointerUp);
    return () => {
      window.clearInterval(tick);
      window.removeEventListener('pointermove', handlePointerMove);
      window.removeEventListener('pointerup', handlePointerUp);
    };
  }, [desktopMode]);

  useEffect(() => {
    if (!desktopMode) return undefined;
    function handlePointerMove(event) {
      const activeDrag = iconDragRef.current;
      if (!activeDrag) return;
      const nextX = Math.max(8, Math.min(event.clientX - activeDrag.offsetX, window.innerWidth - 96));
      const nextY = Math.max(8, Math.min(event.clientY - activeDrag.offsetY, window.innerHeight - 164));
      if (Math.abs(nextX - activeDrag.origin.x) > 4 || Math.abs(nextY - activeDrag.origin.y) > 4) {
        activeDrag.moved = true;
      }
      setDesktopIconPositions(prev => {
        const next = { ...prev, [activeDrag.id]: { x: nextX, y: nextY } };
        localStorage.setItem(DESKTOP_ICON_KEY, JSON.stringify(next));
        return next;
      });
    }
    function handlePointerUp() {
      if (iconDragRef.current?.moved) {
        iconDragMovedRef.current = true;
        window.setTimeout(() => {
          iconDragMovedRef.current = false;
        }, 0);
      }
      iconDragRef.current = null;
    }
    window.addEventListener('pointermove', handlePointerMove);
    window.addEventListener('pointerup', handlePointerUp);
    return () => {
      window.removeEventListener('pointermove', handlePointerMove);
      window.removeEventListener('pointerup', handlePointerUp);
    };
  }, [desktopMode]);

  useEffect(() => {
    if (!desktopMode) return undefined;
    function handleCloseMenus(event) {
      if (startMenuRef.current && !startMenuRef.current.contains(event.target)) {
        setStartMenuOpen(false);
      }
    }
    document.addEventListener('mousedown', handleCloseMenus);
    return () => document.removeEventListener('mousedown', handleCloseMenus);
  }, [desktopMode]);

  useEffect(() => {
    let resolved = false;
    const authTimeout = setTimeout(() => {
      if (!resolved) {
        console.warn('Auth state callback did not fire in time, falling back to unauthed.');
        setAuthState('unauthed');
      }
    }, 8000);

    const unsub = onAuthStateChanged(auth, async currentUser => {
      resolved = true;
      clearTimeout(authTimeout);
      try {
        if (currentUser) {
          setUser(currentUser);
          setAuthState('authed');

          const displayName = currentUser.displayName || currentUser.email?.split('@')[0] || 'User';
          posthog.identify(currentUser.uid, { name: displayName });

          const [loadedWorks, loadedWorkspaces] = await Promise.all([
            getWorks(currentUser.uid),
            getWorkspaces(currentUser.uid),
          ]);

          setWorks(loadedWorks);
          setWorkspaces(loadedWorkspaces);
          await ensureLeaderboardEntry(currentUser.uid, displayName, getInitials(currentUser));
          setMyPoints(await getMyPoints(currentUser.uid));

          const nav = readNav();
          if (nav.page === 'work' && nav.workId) {
            const work = loadedWorks.find(item => item.id === nav.workId);
            if (work) setOpenWork(work);
            else setPage('dashboard');
          }
          if (nav.page === 'publicProfile' && nav.publicUid) {
            setPublicUid(nav.publicUid);
          }
        } else {
          const isGuest = localStorage.getItem(GUEST_KEY) === '1';
          if (isGuest) {
            setUser(null);
            setAuthState('guest');
            setWorks(await getWorks(null));
            setGuestPreferences(getLocalPreferences());
          } else {
            setUser(null);
            setAuthState('unauthed');
            setPage('dashboard');
          }
        }
      } catch (error) {
        console.error('Auth initialization failed:', error);
        setUser(null);
        setAuthState('unauthed');
      }
    });

    return () => {
      clearTimeout(authTimeout);
      unsub();
    };
  }, []);

  async function refreshPoints() {
    if (!user?.uid) return;
    setMyPoints(await getMyPoints(user.uid));
  }

  function handleGuest() {
    posthog.capture('guest_mode_started');
    localStorage.setItem(GUEST_KEY, '1');
    setAuthState('guest');
    const stored = JSON.parse(localStorage.getItem('gwd_data') || '{"works":[]}');
    setWorks(stored.works || []);
    setGuestPreferences(getLocalPreferences());
  }

  function handleWorkUpdate(updated) {
    setWorks(prev => prev.map(work => work.id === updated.id ? updated : work));
    if (openWork?.id === updated.id) setOpenWork(updated);
  }

  async function handleSignOut() {
    posthog.reset();
    localStorage.removeItem(GUEST_KEY);
    sessionStorage.removeItem(NAV_KEY);
    setAuthState('unauthed');
    setUser(null);
    setWorks([]);
    setWorkspaces([]);
    setMyPoints(null);
    setOpenWork(null);
    setPublicUid(null);
    setPage('dashboard');

    if (authState !== 'guest') {
      try { await signOut(auth); } catch (e) {}
    }
  }

  function openWorkPage(work) {
    const stamped = { ...work, updatedAt: Date.now() };
    setOpenWork(stamped);
    navigateToPage('work', { workId: work.id, title: work.title });
  }

  function openPublicProfile(uid) {
    setPublicUid(uid);
    navigateToPage('publicProfile', { publicUid: uid });
  }

  function goBack() {
    if (page === 'work' && user) refreshPoints();
    setPublicUid(null);
    setOpenWork(null);
    navigateToPage('dashboard');
  }

  function toggleSidebar() {
    setSidebarCollapsed(collapsed => {
      const next = !collapsed;
      localStorage.setItem(SIDEBAR_KEY, next);
      return next;
    });
  }

  useEffect(() => {
    if (page === 'notebooks' && !notebooksEnabled) {
      setPage('dashboard');
    }
  }, [page, notebooksEnabled]);

  if (authState === 'loading') {
    return (
      <div className="app" style={{ alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12 }}>
          <div className="loading-spinner" />
          <div style={{ color: 'var(--md-outline)', fontSize: 13 }}>Loading...</div>
        </div>
      </div>
    );
  }

  const uid = user?.uid || null;

  function openDesktopApp(appId) {
    setSelectedDesktopIcon(appId);
    setStartMenuOpen(false);
    navigateToPage(appId);
  }

  function startDesktopIconDrag(iconId, event) {
    const currentPosition = desktopIconPositions[iconId] || DESKTOP_ICON_DEFAULTS[iconId] || { x: 24, y: 24 };
    iconDragRef.current = {
      id: iconId,
      offsetX: event.clientX - currentPosition.x,
      offsetY: event.clientY - currentPosition.y,
      origin: currentPosition,
      moved: false,
    };
  }

  const desktopThemeMenuItems = Object.entries(themes).filter(([key]) => DESKTOP_THEME_OPTIONS.includes(key));


  function handleWallpaperSelect(value) {
    if (value === null) {
      localStorage.removeItem(DESKTOP_WALLPAPER_KEY);
      setDesktopWallpaper(null);
    } else {
      localStorage.setItem(DESKTOP_WALLPAPER_KEY, value);
      setDesktopWallpaper(value);
    }
  }

  function handleWallpaperUpload(event) {
    const file = event.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = reader.result;
      localStorage.setItem(DESKTOP_WALLPAPER_KEY, dataUrl);
      setDesktopWallpaper(dataUrl);
    };
    reader.readAsDataURL(file);
    event.target.value = '';
  }

  function toggleDesktopThemeVariant() {
    if (theme === 'desktop-dark') setTheme('desktop-light');
    else if (theme === 'desktop-light') setTheme('desktop-dark');
    else setTheme('desktop-dark');
  }

  const filteredDesktopIcons = startMenuSearch
    ? desktopIcons.filter(item => item.label.toLowerCase().includes(startMenuSearch.toLowerCase()))
    : desktopIcons;

  const desktopContent = {
    dashboard: (
      <Dashboard
        works={works}
        setWorks={setWorks}
        uid={uid}
        onOpenWork={openWorkPage}
        onOpenProfile={() => navigateToPage('profile')}
        workspaces={workspaces}
        setWorkspaces={setWorkspaces}
        activeWsId={activeWsId}
        setActiveWsId={setActiveWsId}
        showAddWork={showAddWork}
        setShowAddWork={setShowAddWork}
      />
    ),
    upcoming: (
      <UpcomingPage works={works} onOpenWork={openWorkPage} workspaces={workspaces} />
    ),
    pomodoro: (
      <PomodoroTimer works={works} onWorkUpdate={handleWorkUpdate} uid={uid} />
    ),
    'expense-tracker': (
      <ExpenseTracker uid={uid} user={user} isGuest={authState === 'guest'} />
    ),
    profile: (
      <ProfilePage user={user} isGuest={authState === 'guest'} uid={uid} myPoints={myPoints} onViewPublicProfile={openPublicProfile} />
    ),
    leaderboard: (
      <Leaderboard uid={uid} myPoints={myPoints} onViewProfile={openPublicProfile} />
    ),
    settings: (
      <SettingsPage
        user={user}
        myPoints={myPoints}
        isGuest={authState === 'guest'}
        onPointsRefresh={refreshPoints}
        onPreferencesChange={setGuestPreferences}
      />
    ),
    notebooks: (
      <NotebooksPage
        uid={uid}
        user={user}
        isGuest={authState === 'guest'}
        myPoints={myPoints}
        onExit={() => minimizeDesktopWindow('notebooks')}
        onOpenProfile={() => navigateToPage('profile')}
        onOpenSettings={() => navigateToPage('settings')}
        onOpenLeaderboard={() => navigateToPage('leaderboard')}
        onSignOut={handleSignOut}
        socialsEnabled={notebookSocialsEnabled}
      />
    ),
    work: openWork ? (
      <WorkPage key={openWork.id} work={openWork} uid={uid} myPoints={myPoints} onBack={() => closeDesktopWindow('work')} onWorkUpdate={handleWorkUpdate} />
    ) : null,
    publicProfile: publicUid ? (
      <PublicProfilePage targetUid={publicUid} myUid={uid} myName={user?.displayName || user?.email?.split('@')[0] || 'User'} myInitials={getInitials(user)} />
    ) : null,
  };

  if (desktopMode) {
    const wallpaperStyle = {};
    if (desktopWallpaper) {
      if (desktopWallpaper.startsWith('data:') || desktopWallpaper.startsWith('http') || desktopWallpaper.startsWith('blob:')) {
        wallpaperStyle.backgroundImage = `url(${desktopWallpaper})`;
        wallpaperStyle.backgroundSize = 'cover';
        wallpaperStyle.backgroundPosition = 'center';
      } else {
        wallpaperStyle.background = desktopWallpaper;
      }
    }

    return (
      <div className={`desktop-mode-app ${win10Mode ? 'win10' : 'classic'}`}>
        <div className="desktop-wallpaper" style={wallpaperStyle} />
        {classicDesktopMode && <div className="desktop-pixel-clouds" />}
        {classicDesktopMode && <div className="desktop-pixel-hills" />}
        {classicDesktopMode && <div className="desktop-pixel-ground" />}

        <div className="desktop-icons">
          {desktopIcons.map(item => {
            const Icon = item.icon;
            const iconPosition = desktopIconPositions[item.id] || DESKTOP_ICON_DEFAULTS[item.id] || { x: 24, y: 24 };
            return (
              <button
                key={item.id}
                type="button"
                className={`desktop-icon ${selectedDesktopIcon === item.id ? 'selected' : ''}`}
                style={{ transform: `translate3d(${iconPosition.x}px, ${iconPosition.y}px, 0)` }}
                onPointerDown={event => startDesktopIconDrag(item.id, event)}
                onDoubleClick={() => openDesktopApp(item.id)}
                onClick={() => {
                  setSelectedDesktopIcon(item.id);
                  if (!iconDragMovedRef.current) {
                    focusDesktopWindow(item.id);
                  }
                }}
              >
                <span className="desktop-icon-badge">
                  <Icon size={24} />
                </span>
                <span>{item.label}</span>
              </button>
            );
          })}
        </div>

        <div className="desktop-window-layer">
          {visibleDesktopWindows.map(windowState => (
            <DesktopWindowFrame
              key={windowState.id}
              windowState={windowState}
              isFocused={focusedDesktopWindowId === windowState.id}
              onFocus={focusDesktopWindow}
              onMinimize={minimizeDesktopWindow}
              onToggleMaximize={toggleDesktopWindowMaximize}
              onClose={windowId => {
                if (windowId === 'work') {
                  setOpenWork(null);
                  setPage('dashboard');
                }
                if (windowId === 'publicProfile') {
                  setPublicUid(null);
                  setPage('dashboard');
                }
                closeDesktopWindow(windowId);
              }}
              onDragStart={startDesktopDrag}
            >
              {desktopContent[windowState.id]}
            </DesktopWindowFrame>
          ))}
        </div>

        {/* Personalize Overlay */}
        {showPersonalize && (
          <div className="desktop-personalize-overlay" onClick={() => setShowPersonalize(false)}>
            <div className="desktop-personalize-panel" onClick={e => e.stopPropagation()}>
              <header className="desktop-personalize-header">
                <Image size={20} />
                <span>Personalize</span>
                <button type="button" className="desktop-personalize-close" onClick={() => setShowPersonalize(false)}>
                  <X size={16} />
                </button>
              </header>
              <div className="desktop-personalize-body">
                <div className="desktop-personalize-label">Wallpaper Presets</div>
                <div className="desktop-personalize-grid">
                  {WALLPAPER_PRESETS.map(preset => (
                    <button
                      key={preset.id}
                      type="button"
                      className={`desktop-wallpaper-preset ${desktopWallpaper === preset.value ? 'active' : ''} ${!preset.value && !desktopWallpaper ? 'active' : ''}`}
                      style={{ background: preset.value || 'var(--desktop-wallpaper, linear-gradient(135deg, #0078d4, #00bcf2))' }}
                      onClick={() => handleWallpaperSelect(preset.value)}
                    >
                      <span>{preset.name}</span>
                    </button>
                  ))}
                </div>
                <div className="desktop-personalize-label">Custom Image</div>
                <button type="button" className="desktop-upload-btn" onClick={() => wallpaperFileRef.current?.click()}>
                  <Upload size={16} />
                  <span>Upload Wallpaper</span>
                </button>
                <input
                  ref={wallpaperFileRef}
                  type="file"
                  accept="image/*"
                  style={{ display: 'none' }}
                  onChange={handleWallpaperUpload}
                />
              </div>
            </div>
          </div>
        )}

        <footer className="desktop-taskbar">
          <div className="desktop-start-wrap" ref={startMenuRef}>
            <button type="button" className="desktop-start-button" onClick={() => { setStartMenuOpen(open => !open); setStartMenuSearch(''); }}>
              <Monitor size={16} />
            </button>
            {startMenuOpen && (
              <div className="desktop-start-menu">
                <div className="desktop-start-header">
                  <div className="desktop-start-search">
                    <Search size={14} />
                    <input
                      type="text"
                      placeholder="Search apps..."
                      value={startMenuSearch}
                      onChange={e => setStartMenuSearch(e.target.value)}
                      autoFocus
                    />
                  </div>
                </div>
                <div className="desktop-start-pinned">
                  <div className="desktop-start-label">Pinned</div>
                  <div className="desktop-start-tiles">
                    {filteredDesktopIcons.map(item => {
                      const Icon = item.icon;
                      return (
                        <button key={item.id} type="button" className="desktop-start-tile" onClick={() => openDesktopApp(item.id)}>
                          <Icon size={22} />
                          <span>{item.label}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>
                <div className="desktop-start-section">
                  <div className="desktop-start-label">Desktop Themes</div>
                  {desktopThemeMenuItems.map(([key, value]) => (
                    <button key={key} type="button" className={`desktop-start-item ${theme === key ? 'active' : ''}`} onClick={() => setTheme(key)}>
                      <span className="desktop-theme-preview" style={{ background: value.primary }} />
                      <span>{value.name}</span>
                    </button>
                  ))}
                </div>
                <div className="desktop-start-section">
                  <button type="button" className="desktop-start-item" onClick={() => { setShowPersonalize(true); setStartMenuOpen(false); }}>
                    <Palette size={16} />
                    <span>Personalize</span>
                  </button>
                </div>
                <div className="desktop-start-footer">
                  <div className="desktop-start-user">
                    <UserCircle2 size={28} />
                    <span>{user?.displayName || user?.email?.split('@')[0] || 'Guest'}</span>
                  </div>
                  <button type="button" className="desktop-start-item" onClick={() => setTheme('github-dark')}>
                    <LogOut size={16} />
                    <span>Exit Desktop</span>
                  </button>
                </div>
              </div>
            )}
          </div>
          <div className="desktop-taskbar-apps">
            {desktopIcons.map(item => {
              const Icon = item.icon;
              const windowState = desktopWindows.find(entry => entry.id === item.id);
              const isOpen = Boolean(windowState);
              const isActive = focusedDesktopWindowId === item.id && !windowState?.minimized;
              return (
                <button
                  key={item.id}
                  type="button"
                  className={`desktop-taskbar-item ${isOpen ? 'open' : ''} ${isActive ? 'active' : ''}`}
                  title={item.label}
                  onClick={() => {
                    if (!isOpen) {
                      navigateToPage(item.id);
                      return;
                    }
                    if (windowState.minimized) {
                      focusDesktopWindow(item.id);
                      return;
                    }
                    minimizeDesktopWindow(item.id);
                  }}
                >
                  <Icon size={18} />
                </button>
              );
            })}
            {openWork && (
              <button
                type="button"
                className={`desktop-taskbar-item ${desktopWindows.some(item => item.id === 'work') ? 'open' : ''} ${focusedDesktopWindowId === 'work' ? 'active' : ''}`}
                title={openWork.title}
                onClick={() => {
                  if (!desktopWindows.some(item => item.id === 'work')) {
                    openDesktopWindow('work', openWork.title);
                    return;
                  }
                  const workWindow = desktopWindows.find(item => item.id === 'work');
                  if (workWindow?.minimized) {
                    focusDesktopWindow('work');
                    return;
                  }
                  minimizeDesktopWindow('work');
                }}
              >
                <FolderKanban size={18} />
              </button>
            )}
          </div>
          <div className="desktop-system-tray">
            {win10Mode && (
              <button type="button" className="desktop-tray-btn" onClick={toggleDesktopThemeVariant} title={theme === 'desktop-dark' ? 'Switch to Light' : 'Switch to Dark'}>
                {theme === 'desktop-dark' ? <Sun size={16} /> : <Moon size={16} />}
              </button>
            )}
            <div className="desktop-clock">
              <strong>{desktopNow.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}</strong>
              <span>{desktopNow.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}</span>
            </div>
          </div>
        </footer>
      </div>
    );
  }

  return (
    <div className="app">
      {loggedIn && !inProductApp && (
          <Topbar
            user={user}
            myPoints={myPoints}
            isGuest={authState === 'guest'}
            theme={theme}
            setTheme={setTheme}
            showBack={page !== 'dashboard'}
            onBack={goBack}
            onHome={goBack}
            onSignOut={handleSignOut}
            onOpenProfile={() => navigateToPage('profile')}
            onOpenLeaderboard={() => navigateToPage('leaderboard')}
            onOpenSettings={() => navigateToPage('settings')}
            onMenuToggle={() => setMobileMenuOpen(true)}
        />
      )}

      <div style={{ flex: 1, display: 'flex', overflow: 'hidden' }}>
        <div className={`sidebar-overlay ${mobileMenuOpen ? 'open' : ''}`} onClick={() => setMobileMenuOpen(false)} />

        {showSidebar && (
          <Sidebar
            collapsed={sidebarCollapsed}
            mobileMenuOpen={mobileMenuOpen}
            setMobileMenuOpen={setMobileMenuOpen}
            onToggle={toggleSidebar}
            works={works}
            workspaces={workspaces}
            activeWsId={activeWsId}
            setActiveWsId={setActiveWsId}
            page={page}
            setPage={navigateToPage}
            onOpenWork={openWorkPage}
            onNewWork={() => { setShowAddWork(true); setMobileMenuOpen(false); }}
            onNewWorkspace={() => {}}
            notebooksEnabled={notebooksEnabled}
          />
        )}

        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          {!loggedIn ? (
            <AuthScreen onGuest={handleGuest} />
          ) : page === 'work' && openWork ? (
            <WorkPage key={openWork.id} work={openWork} uid={uid} myPoints={myPoints} onBack={goBack} onWorkUpdate={handleWorkUpdate} />
          ) : page === 'profile' ? (
            <ProfilePage user={user} isGuest={authState === 'guest'} uid={uid} myPoints={myPoints} onViewPublicProfile={openPublicProfile} />
          ) : page === 'leaderboard' ? (
            <Leaderboard uid={uid} myPoints={myPoints} onViewProfile={openPublicProfile} />
          ) : page === 'settings' ? (
            <SettingsPage
              user={user}
              myPoints={myPoints}
              isGuest={authState === 'guest'}
              onPointsRefresh={refreshPoints}
              onPreferencesChange={setGuestPreferences}
            />
          ) : page === 'publicProfile' && publicUid ? (
            <PublicProfilePage targetUid={publicUid} myUid={uid} myName={user?.displayName || user?.email?.split('@')[0] || 'User'} myInitials={getInitials(user)} />
          ) : page === 'pomodoro' ? (
            <PomodoroTimer works={works} onWorkUpdate={handleWorkUpdate} uid={uid} />
          ) : page === 'expense-tracker' ? (
            <ExpenseTracker uid={uid} user={user} isGuest={authState === 'guest'} />
          ) : page === 'notebooks' ? (
            <NotebooksPage
              uid={uid}
              user={user}
              isGuest={authState === 'guest'}
              myPoints={myPoints}
              onExit={() => setPage('dashboard')}
              onOpenProfile={() => setPage('profile')}
              onOpenSettings={() => setPage('settings')}
              onOpenLeaderboard={() => setPage('leaderboard')}
              onSignOut={handleSignOut}
              socialsEnabled={notebookSocialsEnabled}
            />
          ) : page === 'upcoming' ? (
            <UpcomingPage works={works} onOpenWork={openWorkPage} workspaces={workspaces} />
          ) : (
            <Dashboard
              works={works}
              setWorks={setWorks}
              uid={uid}
              onOpenWork={openWorkPage}
              onOpenProfile={() => navigateToPage('profile')}
              workspaces={workspaces}
              setWorkspaces={setWorkspaces}
              activeWsId={activeWsId}
              setActiveWsId={setActiveWsId}
              showAddWork={showAddWork}
              setShowAddWork={setShowAddWork}
            />
          )}
        </div>
      </div>

      {loggedIn && !inProductApp && (
        <FriendsPanel user={user} isGuest={authState === 'guest'} myPoints={myPoints} onViewProfile={openPublicProfile} />
      )}

      {loggedIn && !inProductApp && (
        <BottomNav page={page} setPage={navigateToPage} onMenuToggle={() => setMobileMenuOpen(true)} />
      )}
    </div>
  );
}
